// Line-level diff with surrounding context, like a minimal `git diff`.
// Used by the AI edit-proposal card to show just what would change rather
// than the full document. Trailing newlines on the inputs are tolerated.
import { diffLines } from "diff";

export type HunkLine =
  | { kind: "context"; text: string; oldNo: number; newNo: number }
  | { kind: "add"; text: string; newNo: number }
  | { kind: "del"; text: string; oldNo: number };

export type Hunk = {
  lines: HunkLine[];
  oldStart: number;
  newStart: number;
};

export function computeHunks(
  oldText: string,
  newText: string,
  context = 2,
): Hunk[] {
  // ignoreWhitespace: AI providers love to introduce trailing-space
  //   Markdown line breaks; we don't want those to drown the real edit.
  // stripTrailingCr / ignoreNewlineAtEof: defensive against CRLF and
  //   inconsistent final-newline handling between provider outputs.
  const changes = diffLines(oldText, newText, {
    ignoreWhitespace: true,
    stripTrailingCr: true,
    ignoreNewlineAtEof: true,
  });

  type Tok = HunkLine;
  const tokens: Tok[] = [];
  let oldNo = 1;
  let newNo = 1;
  for (const c of changes) {
    const value = c.value;
    const lines = value.split("\n");
    // `diffLines` keeps trailing newlines, which split produces as an
    // empty final element. Drop it so we don't synthesize a phantom line.
    if (lines.length > 0 && lines[lines.length - 1] === "") lines.pop();
    for (const text of lines) {
      if (c.added) {
        tokens.push({ kind: "add", text, newNo });
        newNo++;
      } else if (c.removed) {
        tokens.push({ kind: "del", text, oldNo });
        oldNo++;
      } else {
        tokens.push({ kind: "context", text, oldNo, newNo });
        oldNo++;
        newNo++;
      }
    }
  }

  const changeIdxs: number[] = [];
  for (let i = 0; i < tokens.length; i++) {
    if (tokens[i]!.kind !== "context") changeIdxs.push(i);
  }
  if (changeIdxs.length === 0) return [];

  // Group adjacent changes whose contexts overlap into a single hunk.
  // Two changes belong together if `context` lines after one reach
  // `context` lines before the next, i.e. their index gap is <= context*2 + 1.
  const groups: number[][] = [[changeIdxs[0]!]];
  for (let k = 1; k < changeIdxs.length; k++) {
    const cur = changeIdxs[k]!;
    const last = groups[groups.length - 1]!;
    const prev = last[last.length - 1]!;
    if (cur - prev <= context * 2 + 1) last.push(cur);
    else groups.push([cur]);
  }

  return groups.map((g) => {
    const start = Math.max(0, g[0]! - context);
    const end = Math.min(tokens.length, g[g.length - 1]! + context + 1);
    const lines = tokens.slice(start, end);
    let oldStart = 1;
    let newStart = 1;
    for (const l of lines) {
      if (l.kind !== "add") {
        oldStart = l.oldNo;
        break;
      }
    }
    for (const l of lines) {
      if (l.kind !== "del") {
        newStart = l.newNo;
        break;
      }
    }
    return { lines, oldStart, newStart };
  });
}
